import React, { useEffect, useState } from "react";
import { filterIcon, plusIcon, searchIcon } from "../../assets";
import { TEXT_CONSTANTS } from "../../utils/text-constants/textConstants";
import MenuDropdown from "../../component-lib/Molecules/menuDropdown/MenuDropdown";
import WrapperCard from "../../component-lib/Atoms/card/EventEHRWrapperCard";
import IconButton from "../../component-lib/Molecules/icon-button/EventEHRIconButton";
import Button from "../../component-lib/Molecules/button/Button";
import Input from "../../component-lib/Atoms/Input/Input";
import Filter from "./filter/TaskFilter";
import Table from "../../component-lib/Organisms/table/EventEHRTable";
import AddTasksDialog from "./dialogs/AddTasksDialog";
import {
  deleteTasks,
  getTaskList,
  postTaskData,
  updateTaskData,
} from "./taskSaga";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { componentKey, setCloseAddTaskDialog } from "./taskSlice";
import ViewTaskDialog from "./dialogs/ViewTaskDialog";
import {
  destructStatusData,
  getStatusBackground,
  getStatusDotColor,
  getStatusTextColor,
} from "../../utils/statusConstants";
import {
  getLoginUserData,
  getLoginUserId,
  getUserPermission,
} from "../../utils/getToken";
import TooltipForTable from "../../component-lib/Molecules/tooltip/TooltipForTable";
import EventEHRToggleTabButton from "../../component-lib/Molecules/toggle-tab-button/EventEHRToggleTabButton";
import GroupsScreen from "./group/GroupsScreen";
import CreateGroupDialog from "./group/dialogs/CreateGroupDialog";
import EventEHRConfirmationDialog from "../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";

const TasksScreen = () => {
  const { closeAddTaskDialog, taskListData } = useSelector(
    (state: any) => state[componentKey],
  );

  const userPermissionData: any = getUserPermission();

  const parseLoginUserId: any = getLoginUserId();
  const loginUserId = JSON.parse(parseLoginUserId);
  const dispatch = useDispatch();
  const location = useLocation();
  // When opened from a task notification, pre-filter to tasks assigned to me.
  const assignedToMe = (location?.state as any)?.assignedToMe;
  // When opened from a "New Group Task" notification, start on the Group tab.
  const initialView =
    (location?.state as any)?.view === "Group" ? "Group" : "Tasks";
  // Current user's display name, used to pre-fill the filter's "Assign To".
  const loggedInUserName = (() => {
    try {
      const raw: any = getLoginUserData();
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      return parsed?.name || "";
    } catch {
      return "";
    }
  })();
  const prefilledAssignTo =
    assignedToMe && loginUserId
      ? { label: loggedInUserName, value: loginUserId }
      : null;
  // Task deletion is restricted to SuperAdmin. Deliberately stricter than the
  // Delete Visit rule, which also admits Admin + isAdminAccess. Everyone else
  // gets no Delete menu item, no selection column and no bulk button.
  const canDeleteTask = (() => {
    try {
      const raw: any = getLoginUserData();
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      return parsed?.roleNames?.includes("SuperAdmin") === true;
    } catch {
      return false;
    }
  })();
  // USING THIS HOOK SET INITIAL ROW PER
  const [rowPerPage, setRowPerPage] = useState("10");
  const [openAddTaskDialog, setOpenAddTaskDialog] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [openViewTaskDialog, setOpenViewTaskDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState();
  const [defaultTaskDataRow, setDefaultTaskDataRow] = useState<any>({});
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [selectedValue, setSelectedValue] = useState<any>(null);
  const [pageCount, setPageCount] = useState(1);
  const [resetFlag, setResetFlag] = useState(false);
  // The list defaults to outstanding work (status=NotCompleted). The
  // notification deep-link keeps its own scope so it can't hide the very task
  // the user tapped through to.
  const [isPaginationApplied, setIsPaginationApplied] = useState<{
    isTrue: boolean;
    filterData: Record<string, any>;
  }>(
    assignedToMe
      ? { isTrue: true, filterData: { assigenTo: loginUserId } }
      : { isTrue: false, filterData: { status: "NotCompleted" } },
  );
  // Task deletion (SuperAdmin only). `tasksToDelete` holds the ids the
  // confirmation dialog will act on — one id from the kebab, many from the
  // checkbox selection. `deleteVersion` remounts the Table to clear its
  // internal selection state, which it otherwise keeps across pages/deletes.
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [tasksToDelete, setTasksToDelete] = useState<string[]>([]);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [deleteVersion, setDeleteVersion] = useState(0);
  const [currentView, setCurrentView] = useState(initialView); // "Tasks" or "Group"
  const [openCreateGroupDialog, setOpenCreateGroupDialog] = useState(false);
  const [groupSearchKey, setGroupSearchKey] = useState("");
  const [debouncedGroupSearch, setDebouncedGroupSearch] = useState("");

  // Debounced search for groups
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedGroupSearch(groupSearchKey);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [groupSearchKey]);

  const defaultTaskData = {
    taskType: "",
    assignBy: "",
    assignTo: "",
    assignedDate: "",
    dueDate: "",
    status: "",
  };

  const profileColumns = [
    {
      field: "taskType",
      label: <div>Task Type</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            <TooltipForTable label={row?.taskType} />
          </p>
        );
      },
    },

    {
      field: "description",
      label: <div>Description</div>,
      renderLogic: (row: any) => {
        return <TooltipForTable label={row?.description || "-"} />;
      },
    },
    {
      field: "assignTo",
      label: <div>Assign To</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.assignTo}</p>;
      },
    },

    {
      field: "assignedDate",
      label: <div>Assigned Date</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.assignedDate}</p>
        );
      },
    },
    {
      field: "dueDate",
      label: <div>Due Date</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.dueDate}</p>;
      },
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: any) => {
        return (
          <div className="text-neutral-90 font-normal flex justify-start gap-1 items-center">
            <span
              className="font-weight-400 font-size-12px flex gap-1 items-center"
              style={{
                color: getStatusTextColor(row?.status),
                background: getStatusBackground(row?.status),
                borderRadius: "46px",
                padding: "4px 12px",
              }}
            >
              <span
                className="w-2 h-2"
                style={{
                  background: getStatusDotColor(row?.status),
                  borderRadius: "50%",
                }}
              ></span>
              {destructStatusData(row?.status)}
            </span>
          </div>
        );
      },
    },
    {
      field: "completedAt",
      label: <div>Completed At</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.completedAt}</p>
        );
      },
    },

    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => {
        return (
          <>
            <div className="flex items-center">
              <MenuDropdown
                options={[
                  {
                    label: "View Task",
                    onClickCb: () => {
                      setSelectedTask(row); // Set the row data
                      setOpenViewTaskDialog(true); // Open the dialog
                    },
                    disabled: !userPermissionData?.task?.view, // Disable if permission is false or undefined
                  },
                  {
                    label: "Edit Task",
                    onClickCb: () => {
                      setDefaultTaskDataRow(row);
                      setOpenAddTaskDialog(true);
                    },
                    disabled: !userPermissionData?.task?.update, // Disable if permission is false or undefined
                  },
                  // Delete — SuperAdmin only; hidden entirely for everyone else.
                  ...(canDeleteTask
                    ? [
                        {
                          label: "Delete",
                          onClickCb: () => {
                            setTasksToDelete([row?.id]);
                            setOpenDeleteConfirmDialog(true);
                          },
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          </>
        );
      },
    },
  ];

  const taskRowData = taskListData?.tasks?.map((data: any) => {
    // Assigned To
    let assignToDisplay = "-";

    if (data?.assigendTo) {
      assignToDisplay =
        `${data.assigendTo.firstName || ""} ${data.assigendTo.lastName || ""}`.trim();
    } else if (data?.assignedToAdminUser) {
      assignToDisplay =
        `${data.assignedToAdminUser.firstName || ""} ${data.assignedToAdminUser.lastName || ""}`.trim();
    } else if (data?.group) {
      assignToDisplay = `${data.group.name || "-"} (Group)`;
    }

    // Assigned By
    let assignByDisplay = "-";

    if (data?.assigendBy) {
      assignByDisplay =
        `${data.assigendBy.firstName || ""} ${data.assigendBy.lastName || ""}`.trim();
    } else if (data?.assignedByAdminUser) {
      assignByDisplay =
        `${data.assignedByAdminUser.firstName || ""} ${data.assignedByAdminUser.lastName || ""}`.trim();
    }

    return {
      // Flat id — the table's selectIdentifier looks it up directly on the row.
      id: data?.id,
      taskType: data?.taskType || "-",
      assignBy: assignByDisplay,
      assignTo: assignToDisplay,
      description: data?.description || "-",
      assignedDate: data?.assigenDate
        ? moment.utc(data.assigenDate).format("MM/DD/YYYY")
        : "-",
      dueDate: data?.dueDate
        ? moment.utc(data.dueDate).format("MM/DD/YYYY")
        : "-",
      status: data?.status === "InProgress" ? "In Progress" : data?.status,
      completedAt: data?.completedAt
        ? // completedAt is a true instant (set via new Date()) — show in local
          // time so the completion day matches the viewer's clock.
          moment(data.completedAt).format("MM/DD/YYYY")
        : "-",
      rowData: data,
    };
  });

  // CLOSE ADD TASK DIALOG
  useEffect(() => {
    if (closeAddTaskDialog) {
      setOpenAddTaskDialog(false);
      dispatch(setCloseAddTaskDialog(false));
    }
  }, [closeAddTaskDialog]);

  useEffect(() => {
    try {
      const payload = {
        pageNumber: pageNumber,
        limit: rowPerPage,
        ...isPaginationApplied?.filterData,
      };
      dispatch(getTaskList(payload));
    } catch (error) {
      console.log("error", error);
    }
  }, [
    closeAddTaskDialog,
    pageNumber,
    rowPerPage,
    isPaginationApplied?.filterData,
  ]);

  // Handle applying filter
  const handleApplyFilter = (values: any) => {
    setPageNumber(1);
    const payload = {
      // pageNumber: pageNumber,
      // limit: rowPerPage,
      taskType: values?.taskType,
      assigenTo: values?.assignTo,
      status:
        values?.status === "inProgress"
          ? "InProgress"
          : values?.status === "completed"
            ? "Completed"
            : values?.status === "pending"
              ? "Pending"
              : values?.status,
      dueDate: values?.date ? moment(values?.date).format("YYYY-MM-DD") : "",
      // Task-type category (part before " - ") and its exclusion counterpart.
      taskCategory: values?.taskCategory || undefined,
      excludeTaskCategory: values?.excludeTaskCategory || undefined,
    };
    setIsPaginationApplied({ isTrue: true, filterData: { ...payload } });

    // dispatch(getTaskList(payload));
  };

  const handleClearFilter = () => {
    setPageNumber(1);
    setIsPaginationApplied({ isTrue: false, filterData: {} });

    // const payload = {};
    // dispatch(getTaskList(payload));
  };

  // HANDLE DELETE TASKS (one id from the kebab, many from the selection)
  const handleDeleteTasks = () => {
    if (!tasksToDelete?.length) return;
    // Deleting every row on the current page would strand the user on an empty
    // page, so step back one when that happens.
    const remaining = (taskRowData?.length ?? 0) - tasksToDelete.length;
    const nextPage = remaining <= 0 && pageNumber > 1 ? pageNumber - 1 : pageNumber;

    dispatch(
      deleteTasks({
        taskIds: tasksToDelete,
        refreshPayload: {
          pageNumber: nextPage,
          limit: rowPerPage,
          ...isPaginationApplied?.filterData,
        },
      }),
    );

    setPageNumber(nextPage);
    setSelectedRows([]);
    setDeleteVersion((v) => v + 1);
    setOpenDeleteConfirmDialog(false);
    setTasksToDelete([]);
  };

  const handleAddTaskData = (formData: any) => {
    try {
      const payload = {
        assigenTo: formData?.assignTo || undefined,
        groupId: formData?.groupId || undefined,
        // ROLE_BASED_REMOVED (pre-Jamboree): restore to re-enable role assignment.
        // roleId: formData?.roleId || undefined,
        assigenBy: loginUserId || undefined,
        taskType: formData?.taskType || undefined,
        status:
          formData?.status === "inProgress"
            ? "InProgress"
            : `${formData?.status.charAt(0).toUpperCase() + formData?.status.substring(1)}` ||
              undefined,
        dueDate: formData?.dueDate
          ? moment(formData?.dueDate).format("YYYY-MM-DD")
          : undefined,
        assigenDate: moment().format("YYYY-MM-DD"),
        description: formData?.taskDescription || undefined,
      };
      if (defaultTaskDataRow?.status) {
        const taskUpdatePayload = {
          apiPayload: {
            ...payload,
          },
          taskId: defaultTaskDataRow?.rowData?.id,
        };
        dispatch(updateTaskData(taskUpdatePayload));
      } else {
        dispatch(postTaskData(payload));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const assignToDropdownProps = {
    label: "Select Option", // Customize as needed
    dropdownOptions: [], // Provide options for the dropdown
    dropdownSearch: dropdownSearch,
    selectedValue: selectedValue,
    setDropdownSearch: setDropdownSearch,
    setSelectedValue: setSelectedValue,
    setPageCount: setPageCount,
    setResetFlag: setResetFlag,
  };

  return (
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0 flex flex-col"
      // customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="mb-3">
        {/* Heading */}
        <div className={`flex  items-center justify-between w-full`}>
          <div className="flex items-center gap-3">
            {/* <Heading
            title={TEXT_CONSTANTS?.TASKS}
            customStyle={{ color: "#1D1D1D" }}
          /> */}
            {/* Toggle Tab Button */}
            <EventEHRToggleTabButton
              tabs={["Tasks", "Group"]}
              defaultSelected={currentView}
              onToggle={(value) => setCurrentView(value)}
              variant="default"
              customWidth="200px"
            />
          </div>

          <div className={`flex gap-3`}>
            {currentView === "Tasks" ? (
              <>
                {/* Filter Button */}
                <IconButton
                  icon={<img src={filterIcon} alt={"filter-Icon"} />}
                  onClick={() => {
                    setIsFilterVisible(true);
                  }}
                  style={{
                    border: "1px solid #D2D2D2",
                    width: "40px",
                    height: "40px",
                    borderRadius: "4px",
                  }}
                />
                {/* Bulk delete — SuperAdmin only */}
                {canDeleteTask && (
                  <Button
                    disabled={!selectedRows?.length}
                    label={
                      selectedRows?.length
                        ? `Delete Selected (${selectedRows.length})`
                        : "Delete Selected"
                    }
                    variant="outlined"
                    onClickCb={() => {
                      setTasksToDelete(
                        selectedRows
                          .map((r: any) => r?.id)
                          .filter(Boolean) as string[],
                      );
                      setOpenDeleteConfirmDialog(true);
                    }}
                    style={{
                      paddingTop: "6px",
                      paddingBottom: "6px",
                      minWidth: "175px",
                    }}
                  />
                )}
                <Button
                  disabled={!userPermissionData?.task?.create}
                  startIcon={
                    <img
                      src={plusIcon}
                      alt={"search"}
                      className="cursor-pointer w-[19px] h-[19px]"
                    />
                  }
                  label="Add Task"
                  variant="contained"
                  onClickCb={() => {
                    setDefaultTaskDataRow({});
                    setOpenAddTaskDialog(true);
                  }}
                  style={{
                    paddingTop: "6px",
                    paddingBottom: "6px",
                    minWidth: "155px",
                  }}
                ></Button>
              </>
            ) : (
              /* Group View - Show Search Field and Create Group Button */
              <>
                <div className="flex items-center gap-3">
                  <Input
                    placeholder="Search by group name and created by"
                    value={groupSearchKey}
                    onChangeCb={(e) => setGroupSearchKey(e.target.value)}
                    size="small"
                    style={{
                      width: "300px",
                    }}
                    startIcon={
                      <img src={searchIcon} alt="search" className="w-4 h-4" />
                    }
                  />
                </div>
                <Button
                  startIcon={
                    <img
                      src={plusIcon}
                      alt={"search"}
                      className="cursor-pointer w-[19px] h-[19px]"
                    />
                  }
                  label="Create Task Group"
                  variant="contained"
                  onClickCb={() => {
                    setOpenCreateGroupDialog(true);
                  }}
                  style={{
                    paddingTop: "6px",
                    paddingBottom: "6px",
                    minWidth: "155px",
                  }}
                ></Button>
              </>
            )}
          </div>
        </div>
        {/* Main Part */}
      </div>

      <div className="flex flex-col overflow-auto flex-1 min-h-0">
        {currentView === "Tasks" ? (
          <>
            <div style={{ display: isFilterVisible ? "initial" : "none" }}>
              {
                <Filter
                  onApply={handleApplyFilter} // Apply filter handler
                  onClear={handleClearFilter} // Clear filter handler
                  handleClose={() => setIsFilterVisible(false)} // Close the filter modal
                  assignToDropdownProps={assignToDropdownProps} // Add this line to pass the missing prop
                  prefilledAssignTo={prefilledAssignTo} // pre-select current user when opened from a task notification
                />
              }
            </div>

            <div>
              <Table
                // The table owns its selection state and only clears it when
                // isSelectAll flips falsy — remount instead so selections don't
                // survive a page change, a rows-per-page change or a delete.
                key={`tasks-${pageNumber}-${rowPerPage}-${deleteVersion}`}
                columns={profileColumns}
                rows={taskRowData}
                {...(canDeleteTask
                  ? {
                      selectProps: {
                        isSelectAll: true,
                        isSelectable: true,
                        selectIdentifier: "id",
                        onSelectAll: () => {},
                        onSelectRowsCb: (rows: any) => setSelectedRows(rows),
                      },
                    }
                  : {})}
                paginationProps={{
                  isPagination: true,
                  page: pageNumber,
                  totalPages: taskListData?.totalPages,
                  totalCount: taskListData?.totalRecords,
                  limit: rowPerPage,
                  rowsPerPageOptions: [10, 25, 50],
                  onPageChange: (page: any) => {
                    setPageNumber(page);
                  },
                  handleChangeRowsPerPage: (rowPerPage: any) => {
                    setRowPerPage(rowPerPage);
                    setPageNumber(1);
                  },
                }}
                noDataFoundMessage={"No tasks found"}
              />
            </div>
          </>
        ) : (
          /* Group View */
          <GroupsScreen searchKey={debouncedGroupSearch} />
        )}
      </div>

      {/* ADD Task DIALOG */}
      {openAddTaskDialog && (
        <AddTasksDialog
          defaultTaskData={defaultTaskDataRow}
          dialogHeading={defaultTaskDataRow?.status ? "Edit Task" : "Add Task"}
          isOpen={openAddTaskDialog}
          handleClose={() => {
            setOpenAddTaskDialog(false);
          }}
          handleAddTaskData={(formData) => {
            handleAddTaskData(formData);
          }}
        />
      )}

      {/* VIEW TASK DIALOG */}
      {openViewTaskDialog && (
        <ViewTaskDialog
          dialogHeading="View Task Details"
          isOpen={openViewTaskDialog}
          taskData={selectedTask || defaultTaskData}
          handleClose={() => {
            setOpenViewTaskDialog(false); // Close the dialog
          }}
        />
      )}

      {/* DELETE TASK CONFIRMATION DIALOG (kebab passes a single id) */}
      <EventEHRConfirmationDialog
        alertTitle={tasksToDelete.length > 1 ? "Delete Tasks" : "Delete Task"}
        alertDescription={
          tasksToDelete.length > 1
            ? `Are you sure you want to delete these ${tasksToDelete.length} tasks? This action cannot be undone.`
            : "Are you sure you want to delete this task? This action cannot be undone."
        }
        cancelButtonTitle="Cancel"
        submitButtonTitle="Delete"
        open={openDeleteConfirmDialog}
        handleClose={(action) => {
          if (action === "close") {
            setOpenDeleteConfirmDialog(false);
            setTasksToDelete([]);
          } else if (action === "submit") {
            handleDeleteTasks();
          }
        }}
      />

      {/* CREATE GROUP DIALOG */}
      {openCreateGroupDialog && (
        <CreateGroupDialog
          isOpen={openCreateGroupDialog}
          handleClose={() => setOpenCreateGroupDialog(false)}
          handleCreateGroup={(formData) => {
            setOpenCreateGroupDialog(false);
          }}
        />
      )}
    </WrapperCard>
  );
};

export default TasksScreen;
